import { useEffect } from 'react';
import { NoResult } from 'ui/illustrations';
import { EmptyState } from 'ui/emptyState';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import { PaginationFooter } from './PaginationFooter';
import { useOrganizationMembersQueryContext } from '../../contexts/OrganizationMembersQueryContext';
import { useFiltersContext } from '../../contexts/FiltersContext';
import { MembersTableHeader } from './MembersTableHeader';
import { createMemberRow, filterMemberRow, compareMemberColumns, getGridLayout } from './utils';
import { MemberRow } from './types';
import { TableRows } from './TableRows';
import { TableWithBottomBorder, EmptyStateWrapper } from './MembersTable.styled';
import { SkeletonRows } from './SkeletonRows';

export const MembersTable = () => {
  const { search, filters } = useFiltersContext();
  const { sortBy, sortDirection } = useSortingContext();
  const { currentPage, pageSize, setPageSize } = usePaginationContext();
  const { members, isLoading, isMfaAvailable } = useOrganizationMembersQueryContext();

  const gridLayout = getGridLayout(isMfaAvailable);

  const rows = members.map(createMemberRow).filter((row) => filterMemberRow(row, search, filters));

  const sortedRows = [...rows].sort(compareMemberColumns(sortBy as keyof MemberRow, sortDirection));

  const currentRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  ) as MemberRow[];

  const currentPageIds = currentRows?.map((row) => row.id) || [];

  useEffect(() => {
    setPageSize(20);
  }, [setPageSize]);

  return (
    <>
      <TableWithBottomBorder id="organization-members-table">
        <MembersTableHeader
          currentPageIds={currentPageIds}
          gridLayout={gridLayout}
          isMfaAvailable={isMfaAvailable}
        />
        {!currentRows?.length && isLoading && (
          <SkeletonRows gridLayout={gridLayout} numColumns={isMfaAvailable ? 8 : 7} />
        )}
        {currentRows?.length && (
          <TableRows rows={currentRows} gridLayout={gridLayout} isMfaAvailable={isMfaAvailable} />
        )}
        {!currentRows?.length && !isLoading && (
          <EmptyStateWrapper>
            <EmptyState
              title="No members found"
              subtitle="Try adjusting your search or filters to find what you’re looking for."
              Illustration={NoResult}
            />
          </EmptyStateWrapper>
        )}
      </TableWithBottomBorder>
      <PaginationFooter rowsCount={sortedRows.length} />
    </>
  );
};
